import React from "react";
import moment from "moment";
import { styled } from "@material-ui/styles";
import { injectIntl } from "react-intl";
import { IStyledArguments } from "../../../types/styled-arguments";
import messages from "./CalendarComponent.intl";

const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const dayLabelMessages = [
  messages.mondayShort,
  messages.tuesdayShort,
  messages.wednesdayShort,
  messages.thursdayShort,
  messages.fridayShort,
  messages.saturdayShort,
  messages.sundayShort,
];

const Wrapper = styled("div")({
  width: "100%",
});

const Header = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 14,
  marginBottom: 14,
});

const LegendItem = styled("span")<{ $color: string }>(
  ({ $color }: IStyledArguments & { $color: string }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: "0.68rem",
    color: "#5B6B67",
    "&::before": {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: $color,
      display: "inline-block",
    },
  })
);

const DaysRow = styled("div")({
  display: "flex",
  justifyContent: "space-between",
});

const Day = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
});

const WeekdayLabel = styled("span")<{ $today?: boolean }>(
  ({ theme, $today }: IStyledArguments & { $today?: boolean }) => ({
    fontSize: "0.62rem",
    fontWeight: $today ? 700 : 500,
    color: $today ? theme.palette.primary.dark : theme.palette.text.secondary,
  })
);

const DateBadge = styled("span")<{ $training?: boolean; $match?: boolean; $today?: boolean; $past?: boolean }>(
  ({ theme, $training, $match, $today, $past }: IStyledArguments & { $training?: boolean; $match?: boolean; $today?: boolean; $past?: boolean }) => ({
    width: 30,
    height: 30,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 700,
    fontSize: "0.85rem",
    background: $match ? theme.palette.primary.main : $training ? theme.palette.extra.warning : "transparent",
    color: $match || $training ? "#ffffff" : $past ? "rgba(16, 21, 26, 0.3)" : theme.palette.text.primary,
    border: $today && !$match && !$training ? `1.5px solid ${theme.palette.primary.main}` : "none",
  })
);

interface ICalendarComponent {
  nextMatch?: any;
  trainingWeekList?: any;
}

const CalendarComponent = injectIntl<ICalendarComponent>(({ nextMatch, trainingWeekList, intl }) => {
  const today = moment();
  const monday = moment(today).isoWeekday(1);
  const week = Array.from({ length: 7 }, (_, i) => moment(monday).add(i, "days"));

  return (
    <Wrapper>
      <Header>
        <LegendItem $color="#f57c00">{intl.formatMessage(messages.legendTrainings)}</LegendItem>
        <LegendItem $color="#0E7C86">{intl.formatMessage(messages.legendMatches)}</LegendItem>
      </Header>
      <DaysRow>
        {week.map((date, index) => {
          const isToday = date.isSame(today, "day");
          const isPast = date.isBefore(today, "day");
          const isTraining = !!(trainingWeekList && trainingWeekList[dayKeys[index]]);
          const isMatch = !!(nextMatch && date.isSame(moment(nextMatch.date), "day"));

          return (
            <Day key={date.format("YYYY-MM-DD")}>
              <WeekdayLabel $today={isToday}>{intl.formatMessage(dayLabelMessages[index]).slice(0, 1)}</WeekdayLabel>
              <DateBadge $training={isTraining} $match={isMatch} $today={isToday} $past={isPast}>
                {date.date()}
              </DateBadge>
            </Day>
          );
        })}
      </DaysRow>
    </Wrapper>
  );
});

export default CalendarComponent;
